import { FCC, FlatIconButton, useFormatDateTime } from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TravelSegmentFormValues, TravelSegmentIndexResource } from "../../../../shared/types/travelSegment";
import { travelSegmentIdentifier } from "../travelSegmentIdentifier";
import { BaseCard, Information, Location, TravelIcon } from "./SelectableTravelSegmentBaseCard";

const SelectionContainer = styled(BaseCard)`
	display: grid;
	position: relative;
	grid-template-columns: max-content 1fr;

	padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(5)};
	gap: ${({ theme }) => theme.spacing(5)};
	justify-content: center;
`;

const TopRight = styled.div`
	position: absolute;
	right: ${({ theme }) => theme.spacing(2)};
	top: ${({ theme }) => theme.spacing(2)};
`;

interface TimeProps {
	departureDate: string | null;
	departureTime: string | null;
	arrivalDate: string | null;
	arrivalTime: string | null;
}

const TimeRange: FC<TimeProps> = ({ departureDate, departureTime, arrivalDate, arrivalTime }) => {
	const formatDateTime = useFormatDateTime();

	return useMemo(() => {
		const parts = [];
		if (departureDate) {
			const date = formatDateTime(departureDate, {
				day: "2-digit",
				month: "2-digit",
			});
			parts.push(`${date} ${departureTime}`);
		}
		if (arrivalDate) {
			const date = formatDateTime(arrivalDate, {
				day: "2-digit",
				month: "2-digit",
			});

			parts.push(`${date} ${arrivalTime}`);
		}

		return parts.join(" - ");
	}, [formatDateTime, departureTime, arrivalTime, arrivalDate, departureDate]);
};

interface BaseProps {
	onDelete: () => void;
}

const BaseSelectionCard: FCC<BaseProps> = ({ onDelete, children }) => {
	const { t } = useTranslation();

	return (
		<SelectionContainer $isActive={true}>
			<TopRight>
				<FlatIconButton title={t("Remove travel segment from selection")} onClick={onDelete} icon="close" />
			</TopRight>
			{children}
		</SelectionContainer>
	);
};

interface Props {
	travelSegment: TravelSegmentIndexResource | TravelSegmentFormValues;
	onDelete: () => void;
}

export const SelectableTravelSegmentSelectionCard: FC<Props> = ({ travelSegment, onDelete }) => {
	switch (travelSegment.type) {
		case "car":
			return (
				<BaseSelectionCard onDelete={onDelete}>
					<TravelIcon src="/icons/car.svg" />
					<Information>
						<Location title={travelSegmentIdentifier(travelSegment)}>{travelSegmentIdentifier(travelSegment)}</Location>
						<TimeRange
							arrivalTime={travelSegment.arrivalTime}
							arrivalDate={travelSegment.arrivalDate}
							departureTime={travelSegment.departureTime}
							departureDate={travelSegment.departureDate}
						/>
					</Information>
				</BaseSelectionCard>
			);
		case "shuttle":
			return (
				<BaseSelectionCard onDelete={onDelete}>
					<TravelIcon src="/icons/shuttle.svg" />
					<Information>
						<Location title={travelSegmentIdentifier(travelSegment)}>{travelSegmentIdentifier(travelSegment)}</Location>
						<TimeRange
							arrivalTime={travelSegment.arrivalTime}
							arrivalDate={travelSegment.arrivalDate}
							departureTime={travelSegment.departureTime}
							departureDate={travelSegment.departureDate}
						/>
					</Information>
				</BaseSelectionCard>
			);
		case "train":
			return (
				<BaseSelectionCard onDelete={onDelete}>
					<TravelIcon src="/icons/train.svg" />
					<Information>
						<Location title={`${travelSegment.departureLocation} - ${travelSegment.arrivalLocation}`}>
							{travelSegment.departureLocation} - {travelSegment.arrivalLocation}
						</Location>
						<TimeRange
							arrivalTime={travelSegment.arrivalTime}
							arrivalDate={travelSegment.arrivalDate}
							departureTime={travelSegment.departureTime}
							departureDate={travelSegment.departureDate}
						/>
					</Information>
				</BaseSelectionCard>
			);
		case "flight":
			return (
				<BaseSelectionCard onDelete={onDelete}>
					<TravelIcon src="/icons/travel.svg" />
					<Information>
						<Location title={`${travelSegment.departureLocation?.name} - ${travelSegment.arrivalLocation?.name}`}>
							{travelSegment.departureLocation?.name} - {travelSegment.arrivalLocation?.name}
						</Location>
						<TimeRange
							arrivalTime={travelSegment.arrivalTime}
							arrivalDate={travelSegment.arrivalDate}
							departureTime={travelSegment.departureTime}
							departureDate={travelSegment.departureDate}
						/>
					</Information>
				</BaseSelectionCard>
			);
	}
};
